import { useCallback, useState } from "react";

import { CheckCircleOutline, Close, DeleteForever, Edit, HighlightOff } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Drawer, IconButton, Tooltip, Typography } from "@mui/material";


import { IChangeEvent } from "@rjsf/core";
import { Form } from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { JSONSchema7 } from "json-schema";

import PrimaryLayout from "../../components/layout/PrimaryLayout";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import DataTable from "../../components/tables/DataTable";
import { Column } from "../../components/tables/interfaces";
import { sqlpadService } from "../../services/sqlpad-service";
import { Connection, ConnectionDetail, DatasourceEditPayload, DatasourceForm } from "../../utils/models/sqlpad";
import { dataSourceFormSchema, dataSourceFormUiSchema } from "./dataSourceSchema";


const columns: Column[] = [
  { id: "name", label: "Name", type: 'string' },
  { id: "driver", label: "Driver", type: 'string' },
  { id: "created_at", label: "Created At", type: "datetime" },
  { id: "updated_at", label: "Updated At", type: "datetime" },
  { id: "action", label: "Actions", },
];

const DatasourcesList = () => {
  const [openAddDatasourceDrawer, setOpenAddDatasourceDrawer] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [formValue, setFormValue] = useState({});
  const [testConnectionResult, setTestConnectionResult] = useState<boolean | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedConnectionAction, setSelectedConnectionAction] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getDbConnectionsList = async () => {
    try {
      const response = await sqlpadService.getConnectionsList();
      console.log('==>get connections list response: ', response);
      return response;
    } catch (error: any) {
      console.log('error in getting connections list: ', error)
    }
  }

  const handleTestConnection = async () => {
    verifyConnection(formValue as DatasourceForm)
  }

  const handleDeleteConnection = (isDelete: boolean) => {
    if (!isDelete) return;

    removeConnection(selectedConnectionAction);
  }

  const saveConnection = async (payload: DatasourceForm) => {
    setIsLoading(true);

    try {
      const response = await sqlpadService.createConnection(payload);
      console.log('==> create connection response: ', response);
      return response;
    } catch (error: any) {
      console.log('error while creating a connection: ', error);
      alert('some error occurred while saving the connection')
    } finally {
      setIsLoading(false);
    }
  }

  const editConnection = async (connId: string, payload: DatasourceForm) => {
    setIsLoading(true);

    const updatedPayload: DatasourceEditPayload = {
      name: payload.name,
      driver: payload.driver,
      data: {
        database: payload?.database || '',
        host: payload.host,
        password: payload.password,
        port: payload?.port || '',
        username: payload.username,
      }
    }
    try {
      const response = await sqlpadService.updateConnection(connId, updatedPayload);
      console.log('==> update connection response: ', response);
      return response;
    } catch (error: any) {
      console.log('error while updating a connection: ', error);
      alert('some error occurred while updating the connection')
    } finally {
      setIsLoading(false);
    }
  }

  const fetchConnection = async (connId: string) => {
    try {
      const response = await sqlpadService.getConnection(connId);
      console.log('==> get connection response: ', response);
      setFormValue({
        name: response.name,
        driver: response.driver,
        host: response?.host || '',
        port: response?.port || '',
        database: response?.database || '',
        username: response?.username || '',
      } as ConnectionDetail);
    } catch (error: any) {
      console.log('error while get a connection: ', error);
      alert('some error occurred while get the connection');
    }
  }

  const verifyConnection = async (payload: DatasourceForm) => {
    try {
      const response = await sqlpadService.testConnection(payload);
      console.log('==> test connection response: ', response);
      // return response;
      if (response === 200) {
        setTestConnectionResult(true);
      }
    } catch (error: any) {
      console.log('error while testing a connection: ', error);
      setTestConnectionResult(false);
      if (error?.status === 400) {
        return alert(error?.response?.data?.title)
      }
      alert('some error occurred while testing the connection')
    }
  }

  const removeConnection = async (delId: string) => {
    setIsLoading(true);

    try {
      const response = await sqlpadService.deleteConnection(delId);
      console.log('==> delete connection response: ', response);
      if (response === 200) {
        setSelectedConnectionAction('');
        setRefreshKey(prevKey => prevKey + 1);
      }
    } catch (error: any) {
      console.log('error while delete a connection: ', error);
      if (error?.status === 404) {
        return alert(error?.response?.data?.title)
      }
      alert('some error occurred while deleting the connection')
    } finally {
      setIsLoading(false);
    }
  }

  const fetchData = useCallback(async (query?: { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => {
    const response = await getDbConnectionsList();
    console.log('fetched table data: ', response);

    return response || [];
  }, [])

  const dataExtractor = useCallback((response: Connection[]) => {
    return response.map(item => {

      return {
        id: item.id,
        name: item.name,
        driver: item.driver,
        created_at: item.createdAt,
        updated_at: item.updatedAt,
        action: <Box display='flex' gap={1}>
          <>
            <Tooltip title='Edit Connection'>
              <IconButton color='warning' onClick={() => {
                fetchConnection(item.id);
                setSelectedConnectionAction(item.id);
                setIsEditMode(true);
                setOpenAddDatasourceDrawer(true);
              }}>
                <Edit />
              </IconButton>
            </Tooltip>

            <Tooltip title='Delete Connection'>
              <IconButton color='error' onClick={() => {
                setOpenDeleteModal(true);
                setSelectedConnectionAction(item.id);
              }}>
                <DeleteForever />
              </IconButton>
            </Tooltip>
          </>
        </Box>
      }
    })
  }, [])

  const handleFormSubmit = async (formData: IChangeEvent<DatasourceForm, JSONSchema7, any>) => {
    const submittedData = formData.formData;
    if (!submittedData) return;

    console.log("submitted Data", submittedData);

    const connection = isEditMode ? await editConnection(selectedConnectionAction, submittedData) : await saveConnection(submittedData);

    if (connection?.id) {
      setRefreshKey(prevKey => prevKey + 1);
      setOpenAddDatasourceDrawer(false);
      setSelectedConnectionAction('');
    }

    setTestConnectionResult(null);
    setFormValue({});
  }

  return (
    <PrimaryLayout showBreadcrumb={false} transparentLoading={isLoading}>
      <Box sx={{ m: 2, display: 'flex', justifySelf: 'flex-end' }} >
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => {
          setIsEditMode(false);
          setOpenAddDatasourceDrawer(true)
        }} sx={{ mr: 2 }}>
          Add DataSource
        </Button>
      </Box>

      {openAddDatasourceDrawer &&
        <Drawer anchor="right" open={openAddDatasourceDrawer} onClose={() => {
          setOpenAddDatasourceDrawer(false)
          setTestConnectionResult(null);
          setFormValue({});
        }}>
          <Box sx={{ width: 600, p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6">{isEditMode ? "Edit Data Source" : "Add Data Source"}</Typography>
              <IconButton onClick={() => setOpenAddDatasourceDrawer(false)}>
                <Close />
              </IconButton>
            </Box>

            <Form
              schema={dataSourceFormSchema}
              uiSchema={dataSourceFormUiSchema}
              formData={formValue}
              showErrorList={false}
              onSubmit={handleFormSubmit}
              autoComplete="off"
              onError={(errors) => console.log("Form errors:", errors)}
              validator={validator}
              onChange={({ formData }) => setFormValue(formData)}
              transformErrors={(errors) => {
                return errors.map((error) => {
                  if (error.name === "pattern" && error.property === ".connection_name") {
                    error.message = "Please provide a connection name without any special characters"
                  } else if (error.name === "pattern" && error.property === ".port") {
                    error.message = "Please use only numbers";
                  }
                  return error;
                });
              }}
            >
              <Box mt={1.5} display='flex' gap={2}>
                <Button type="submit" variant="contained" sx={{ width: '160px' }} size="large">submit</Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  color={testConnectionResult === null ? undefined : testConnectionResult ? 'success' : 'error'}
                  sx={{ width: '160px' }}
                  onClick={handleTestConnection}
                  startIcon={testConnectionResult === null ? undefined :
                    testConnectionResult ? <CheckCircleOutline /> : <HighlightOff />}
                >
                  test
                </Button>
              </Box>
            </Form>
          </Box>
        </Drawer>
      }

      <Box key={refreshKey}>
        <DataTable
          columns={columns}
          fetchData={fetchData}
          dataExtractor={dataExtractor}
          stickyHeader
          tableContainerMaxHeight={'calc(100vh - 220px)'}
        />

        {openDeleteModal &&
          <ConfirmationModal
            open={openDeleteModal}
            setOpen={setOpenDeleteModal}
            headTitle='Delete'
            bodyMessage='Removing a data source is permanent and cannot be undone. Continue?'
            confirmBtnColor='error'
            handleSubmit={handleDeleteConnection}
          />
        }
      </Box>
    </PrimaryLayout>
  );
};

export default DatasourcesList;